import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

export interface DynamicNavigationProps {
  links: {
    id: string;
    label: string;
    href: string;
    icon?: React.ReactNode;
  }[];
  logo?: React.ReactNode;
  backgroundColor?: string;
  textColor?: string;
  highlightColor?: string;
  glowIntensity?: number;
  className?: string;
  showLabelsOnMobile?: boolean;
  onLinkClick?: (id: string) => void;
  activeLink?: string;
  enableRipple?: boolean;
}

const cn = (...classes: (string | undefined | boolean)[]) =>
  classes.filter((cls): cls is string => typeof cls === "string").join(" ");

export const DynamicNavigation = ({
  links,
  logo,
  backgroundColor,
  textColor,
  highlightColor,
  glowIntensity = 5,
  className = "",
  showLabelsOnMobile = false,
  onLinkClick,
  activeLink,
  enableRipple = true,
}: DynamicNavigationProps) => {
  const navigate = useNavigate();
  const navRef = useRef<HTMLDivElement | null>(null);
  const highlightRef = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState(activeLink || (links.length > 0 ? links[0].id : null));
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isOnHero, setIsOnHero] = useState(true);
  const [isClient, setIsClient] = useState(false);
  const homeLink =
    links.find((link) => link.id.toLowerCase() === "home") ??
    (links.length > 0 ? links[0] : undefined);

  const defaultThemeStyles = {
    bg: backgroundColor
      ? ""
      : isOnHero
      ? "bg-transparent sm:bg-transparent"
      : "bg-white",
    border: isOnHero ? "border border-transparent" : "border border-slate-200",
    text: textColor ? "" : (isOnHero ? "text-white" : "text-slate-800"),
    highlight: highlightColor ? "" : (isOnHero ? "bg-white/15" : "bg-slate-100"),
    glow: "shadow-none",
  };

  const updateHighlightPosition = (id?: string) => {
    if (!navRef.current || !highlightRef.current) return;

    const linkElement = navRef.current.querySelector<HTMLElement>(
      `#nav-item-${id || active}`
    );
    if (!linkElement) return;

    const { left, width } = linkElement.getBoundingClientRect();
    const navRect = navRef.current.getBoundingClientRect();

    highlightRef.current.style.transform = `translateX(${left - navRect.left}px)`;
    highlightRef.current.style.width = `${width}px`;
  };

  const createRipple = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (!enableRipple) return;

    const button = event.currentTarget as HTMLElement | null;
    if (!button || typeof button.appendChild !== "function") return;
    const circle = document.createElement("span");
    const diameter = Math.max(button.clientWidth, button.clientHeight);

    circle.style.width = circle.style.height = `${diameter}px`;
    circle.style.left = `${event.clientX - button.getBoundingClientRect().left - diameter / 2}px`;
    circle.style.top = `${event.clientY - button.getBoundingClientRect().top - diameter / 2}px`;
    if (circle && circle.classList && document.body.contains(circle)) {
      circle.classList.add("absolute", "bg-white", "rounded-full", "pointer-events-none", "opacity-30", "animate-ripple");
    }

    const rippleEls = button.getElementsByClassName("ripple");
    if (rippleEls && rippleEls.length > 0 && typeof rippleEls[0].remove === "function") {
      rippleEls[0].remove();
    }

    button.appendChild(circle);
    setTimeout(() => {
      if (circle && typeof circle.remove === "function") circle.remove();
    }, 600);
  };

  const handleLinkClick = (
    id: string,
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    if (enableRipple) {
      createRipple(event);
    }
    setActive(id);
    if (onLinkClick) {
      onLinkClick(id);
    }
    setIsMenuOpen(false);
  };

  const handleLinkHover = (id: string) => {
    if (!navRef.current || !highlightRef.current) return;
    updateHighlightPosition(id);
  };

  useEffect(() => {
    updateHighlightPosition();

    const handleResize = () => {
      updateHighlightPosition();
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [active, links]);

  useEffect(() => {
    if (activeLink && activeLink !== active) {
      setActive(activeLink);
    }
  }, [activeLink, active]);

  useEffect(() => {
    if (!isMenuOpen) return;

    const previousOverflowY = document.body.style.overflowY;
    document.body.style.overflowY = "hidden";
    return () => {
      document.body.style.overflowY = previousOverflowY;
    };
  }, [isMenuOpen]);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (backgroundColor) return;

    // Throttle function to reduce scroll event frequency
    const throttle = (fn: Function, delay: number) => {
      let lastCall = 0;
      return (...args: any[]) => {
        const now = Date.now();
        if (now - lastCall >= delay) {
          lastCall = now;
          fn(...args);
        }
      };
    };

    const handleScroll = () => {
      const heroSection = document.getElementById("hero");
      if (!heroSection) {
        setIsOnHero(window.scrollY < 60);
        return;
      }

      const heroBottom = heroSection.offsetTop + heroSection.offsetHeight;
      const navHeight = navRef.current?.offsetHeight || 0;
      setIsOnHero(window.scrollY + navHeight < heroBottom);
    };

    const throttledScroll = throttle(handleScroll, 100);

    handleScroll();
    window.addEventListener("scroll", throttledScroll, { passive: true });
    const handleResize = throttle(handleScroll, 100);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", throttledScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [backgroundColor]);

  return (
    <nav
      className={cn(
        `fixed top-0 left-0 right-0 z-50 w-full box-border rounded-none transition-all duration-500 px-4 sm:px-6 py-4`,
        defaultThemeStyles.bg,
        defaultThemeStyles.border,
        defaultThemeStyles.text,
        defaultThemeStyles.glow,
        className
      )}
      style={{
        backgroundColor: backgroundColor,
        color: textColor,
      }}
    >
      <div
        className="container"
        style={{
          maxWidth: '1200px',
          marginLeft: 'auto',
          marginRight: 'auto',
          paddingLeft: '24px',
          paddingRight: '24px',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div className="shrink-0 min-w-[98px] sm:min-w-[128px] flex justify-center items-center">
          {homeLink ? (
            <a
              href={homeLink.href}
              aria-label="Go to home"
              onClick={() => {
                setActive(homeLink.id);
                if (onLinkClick) {
                  onLinkClick(homeLink.id);
                }
                setIsMenuOpen(false);
              }}
              className="inline-flex items-center"
            >
              {logo || (
                <div className="h-9 w-24 rounded-md bg-slate-100" aria-hidden="true" />
              )}
            </a>
          ) : (
            logo || (
              <div className="h-9 w-24 rounded-md bg-slate-100" aria-hidden="true" />
            )
          )}
        </div>

        <button
          type="button"
          className="sm:hidden ml-auto inline-flex h-11 w-11 items-center justify-center rounded-md border border-current bg-white/10 text-current backdrop-blur-sm transition-all duration-500"
          onClick={() => setIsMenuOpen(true)}
          aria-label="Open menu"
        >
          <span className="relative block h-4 w-5">
            <span className="absolute left-0 top-0 h-0.5 w-5 bg-current" />
            <span className="absolute left-0 top-1.5 h-0.5 w-5 bg-current" />
            <span className="absolute left-0 top-3 h-0.5 w-5 bg-current" />
          </span>
        </button>

        <div ref={navRef} className="relative hidden sm:block flex-1">
          <div
            ref={highlightRef}
            className={cn(
              `absolute top-0 left-0 h-full rounded-lg transition-all 
              duration-300 ease-[cubic-bezier(0.25,1,0.5,1)] z-0`,
              defaultThemeStyles.highlight
            )}
            style={{
              backgroundColor: highlightColor,
            }}
          ></div>

          <ul className="flex justify-center items-center gap-3 sm:gap-4 py-1 relative z-10">
            {links.map((link: { id: string; label: string; href: string; icon?: React.ReactNode }) => (
              <li
                key={link.id}
                className="rounded-lg"
                id={`nav-item-${link.id}`}
              >
                <a
                  href={link.href}
                  className={cn(
                    `flex gap-1 items-center justify-center h-11 px-5 sm:px-7 text-base md:text-lg
                    rounded-lg font-medium transition-all duration-300 hover:scale-105 
                    relative overflow-hidden text-current`,
                    active === link.id && "font-semibold"
                  )}
                  onClick={(e) => {
                    e.preventDefault();
                    setActive(link.id);
                    if (onLinkClick) onLinkClick(link.id);
                    else navigate(link.href);
                  }}
                  onMouseEnter={() => handleLinkHover(link.id)}
                >
                  {link.icon && (
                    <span className="text-current text-xs">{link.icon}</span>
                  )}
                  <span
                    className={cn(showLabelsOnMobile || !link.icon ? "flex" : "hidden sm:flex")}
                  >
                    {link.label}
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {isMenuOpen && isClient && createPortal(
        <div
          className="fixed inset-0 z-[9999] sm:hidden animate-mobile-menu-in"
          style={{ backgroundColor: "#000000", color: "#ffffff" }}
        >
          <div
            className="flex h-full flex-col"
            style={{ backgroundColor: "#000000", color: "#ffffff" }}
          >
            <div className="flex items-center justify-between border-b border-white/20 px-5 py-3">
              <span className="text-lg font-semibold text-white">Menu</span>
              <button
                type="button"
                className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-white/30 bg-transparent text-white"
                onClick={() => setIsMenuOpen(false)}
                aria-label="Close menu"
              >
                ✕
              </button>
            </div>

            <ul className="mt-6 flex flex-col gap-2 px-6 pb-6">
              {links.map((link: { id: string; label: string; href: string; icon?: React.ReactNode }, index: number) => (
                <li key={link.id} id={`mobile-nav-item-${link.id}`}>
                  <a
                    href={link.href}
                    className={cn(
                      "flex items-center gap-2 py-3 text-3xl font-semibold transition-all duration-200 animate-mobile-item-in",
                      active === link.id ? "text-white" : "text-white/75 hover:text-white"
                    )}
                    style={{ animationDelay: `${index * 70}ms` }}
                    onClick={(e) => {
                      e.preventDefault();
                      handleLinkClick(link.id, e);
                    }}
                  >
                    {link.icon && <span className="text-current text-sm">{link.icon}</span>}
                    <span>{link.label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>,
        document.body
      )}

      <style
        dangerouslySetInnerHTML={{
          __html: `        @keyframes ripple {
          to {
            transform: scale(4);
            opacity: 0;
          }
        }
        .animate-ripple {
          animation: ripple 0.6s linear;
        }
        @keyframes mobileMenuIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-mobile-menu-in {
          animation: mobileMenuIn 240ms ease-out;
        }
        @keyframes mobileItemIn {
          from {
            opacity: 0;
            transform: translateX(-14px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-mobile-item-in {
          opacity: 0;
          animation: mobileItemIn 280ms ease-out forwards;
        }
`,
        }}
      />
    </nav>
  );
};

export default DynamicNavigation;
